import { SyntheticEvent, useCallback } from "react"
import { useMatchRoute, useRouter } from "@tanstack/react-router"

import { routes } from "#/shared/constants"

// This hook will make sure we are not taking exact URL value for matching route.
// This hook should be used instead of classic useMatchRoute
// - /test => troue
// - /test/ => true
// - /test?value=some_value => true
export const useMatchFullRoute = () => {
  const matchRoute = useMatchRoute()

  const matchRouteHandler = useCallback(
    (route: string) => matchRoute({ to: route, fuzzy: true }),
    [matchRoute],
  )

  return { matchRoute: matchRouteHandler }
}

export const useSinglePageRouterChange = () => {
  const { history } = useRouter()

  const changeRoute = useCallback(
    (
      event: Event | KeyboardEvent | SyntheticEvent<HTMLButtonElement, Event>,
      route: string | undefined,
    ) => {
      event.preventDefault()
      if (!route) return

      history.push(route)
    },
    [history],
  )

  return { changeRoute }
}

export const useCurrentRoute = (href: string) => {
  const { matchRoute } = useMatchFullRoute()
  const isCurrentRoute = !!matchRoute(href)

  return isCurrentRoute
}

export const useHomeRoute = () => {
  const { matchRoute } = useMatchFullRoute()
  const isHomeRoute = !!matchRoute(routes.OVERVIEW)

  return isHomeRoute
}

export const useMarketRoute = () => {
  const { matchRoute } = useMatchFullRoute()

  const isMarketRoute =
    !!matchRoute(routes.MARKET) || !!matchRoute(routes.MARKET_ITEM)

  return isMarketRoute
}

export const useExploreRoute = () => {
  const { matchRoute } = useMatchFullRoute()
  const isExploreRoute = !!matchRoute(routes.EXPLORE)

  return isExploreRoute
}

export const useBorrowRoute = () => {
  const { matchRoute } = useMatchFullRoute()
  const isBorrowRoute = !!matchRoute(routes.BORROW)

  return isBorrowRoute
}

export const useVaultsRoute = () => {
  const { matchRoute } = useMatchFullRoute()
  const isVaultsRoute = !!matchRoute(routes.EXPLORE_VAULTS)

  return isVaultsRoute
}

export const useDAppRoute = () => {
  const { matchRoute } = useMatchFullRoute()

  const dAppRoutes = Object.values(routes).filter(
    (route) =>
      ![routes.ABOUT, routes.REWARDS, routes.TERMS, routes.PRIVACY]
        .filter(Boolean)
        .includes(route),
  )

  const isDAppRoute = dAppRoutes.some((route) => matchRoute(route))

  return isDAppRoute
}

export const useLandingRoute = () => {
  const { matchRoute } = useMatchFullRoute()

  const isLandingRoute =
    matchRoute(routes.BORROW_LANDING) ||
    matchRoute(routes.MUSD_LANDING) ||
    matchRoute(routes.GROWTH_LANDING) ||
    matchRoute(routes.FAQ_LANDING) ||
    matchRoute(routes.LANDING)

  const isLandingHomeRoute = matchRoute(routes.LANDING)

  const isLandingPageRoute =
    matchRoute(routes.BORROW_LANDING) ||
    matchRoute(routes.GROWTH_LANDING) ||
    matchRoute(routes.FAQ_LANDING) ||
    matchRoute(routes.MUSD_LANDING)

  return { isLandingRoute, isLandingHomeRoute, isLandingPageRoute }
}
